import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { MainService } from "core/main.service";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { ActiveFloatingActionButtonType } from "core/volatile-application-state.model";
import { AiService } from "common/ai-content/ai.service";
import { DialogService } from "common/dialog/dialog.service";
import { HttpErrorResponse } from "@angular/common/http";
import { map, takeUntil } from "rxjs/operators";
import { ContentGeneratorResource } from "core/stages-client";
import Prompt = stages.common.Prompt;

@Component({
	selector: "stages-ai-floating-menu",
	templateUrl: "./ai-floating-menu.component.html",
	styleUrls: ["./ai-floating-menu.component.scss"],
	providers: [AiService],
})
export class AiFloatingMenuComponent implements OnDestroy, OnChanges {
	private destroy$ = new Subject<boolean>();

	showFloatingActionButton$ = new Observable<boolean>();
	menuItems: MenuItem[] = [];
	isLoading = false;

	@Input()
	beanType!: string;

	@Input() prompts: Prompt[] = [];

	@Input() selectedText: string | null = null;

	@Output()
	readonly updatedText$ = new EventEmitter<string>();

	constructor(
		private readonly mainService: MainService,
		private readonly dialogService: DialogService,
		private readonly contentGeneratorResource: ContentGeneratorResource,
		private readonly route: ActivatedRoute,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.prompts?.firstChange && changes.prompts.currentValue.length > 0) {
			this.showFloatingActionButton$ = this.mainService.applicationState.activeFloatingActionButtonType.pipe(
				map((activeButton) => {
					const isAllowed = this.mainService.isFloatingActionButtonActivationAllowed(
						activeButton,
						ActiveFloatingActionButtonType.DESCRIPTION,
					);
					if (isAllowed) {
						this.mainService.applicationState.activeFloatingActionButtonType.next(
							ActiveFloatingActionButtonType.DESCRIPTION,
						);
					}
					return isAllowed;
				}),
				takeUntil(this.destroy$),
			);
		}

		if (changes.prompts || changes.selectedText) {
			this.menuItems = this.updateMenu(this.selectedText ?? "");
		}
	}

	ngOnDestroy(): void {
		this.mainService.applicationState.activeFloatingActionButtonType.next(ActiveFloatingActionButtonType.NONE);
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	private updateMenu(selectedText: string): MenuItem[] {
		const menuItems: MenuItem[] = [];
		for (const prompt of this.prompts.filter((p) => this.isVisible(p, selectedText))) {
			menuItems.push({
				name: prompt.name,
				iconClass: prompt.promptType === "DESCRIPTION" ? ["ico-add"] : ["ico-edit-text"],
				id: prompt.id,
				on: async (): Promise<void> => {
					this.isLoading = true;
					try {
						const result =
							prompt.promptType === "DESCRIPTION"
								? await this.contentGeneratorResource.generateDescription(
										this.route.snapshot.paramMap.get("workspaceId")!,
										this.beanType, // elementType is not set in url for management descriptions
										this.route.snapshot.paramMap.get("identity")!,
										{
											pv: this.route.snapshot.paramMap.get("processVersion")!,
											promptId: prompt.id,
										},
								  )
								: await this.contentGeneratorResource.rewriteDescription(
										this.route.snapshot.paramMap.get("workspaceId")!,
										this.beanType,
										this.route.snapshot.paramMap.get("identity")!,
										selectedText,
										{
											pv: this.route.snapshot.paramMap.get("processVersion")!,
											promptId: prompt.id,
										},
								  );

						this.updatedText$.emit(result);
						this.isLoading = false;
					} catch (errorResponse: unknown) {
						if (errorResponse instanceof HttpErrorResponse) {
							await this.dialogService.error("ai.widget.error.generate", errorResponse.error.message);
						}
					} finally {
						this.isLoading = false;
					}
				},
			});
		}
		return menuItems;
	}

	private isVisible(prompt: Prompt, selectedText: string): boolean {
		return (
			(prompt.promptType === "DESCRIPTION" && selectedText.length === 0) ||
			(prompt.promptType === "REWRITE" && selectedText.length > 0)
		);
	}
}
